import React, { useState, useMemo, useEffect } from 'react';
import { Text, Bubble, Tabs } from 'tea-component';
import AggregationBubble from '@/pages/intelligence/components/AggregationBubble';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import SelectChoice from '@/pages/intelligence/components/SelectCompoenet/SelectChoice';
import { useIntl, getLocale } from 'umi';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import { MetricsTablePageIds } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';

import './index.scss';

export type ChangeSegementFunType = {
  metric: string;
  source: string;
  metricname: string;
  changeTarget?: string;
  isChange?: boolean;
  currentMetricObj: { tableSource?: string; [key: string]: any };
  tableSource: string;
};
type PropsTypes = {
  options: any[];
  onChange: (params: ChangeSegementFunType) => void;
  metricValue?: string;
  pageId: string;
  loading: boolean;
};

export default function DataSourceMetricSelector(props: PropsTypes) {
  const { options, onChange, pageId, metricValue, loading } = props;
  const defaultDataSource = Array.isArray(options) && options.length ? options[0] : null;
  const defaultMetric = defaultDataSource?.children?.length ? defaultDataSource.children[0] : null;
  const [dataSource, setDataSource] = useState<any>(defaultDataSource);
  const [metric, setMetric] = useState<any>(defaultMetric);
  const { formatMessage } = useIntlExtend();
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;

  const getMessage = (option: any) =>
    (isCN ? option?.messageCN : option?.message) || option?.message;

  const selectedDataSourceValue = useMemo(() => {
    if (!metricValue) return '';
    if (!Array.isArray(options)) return '';
    for (const option of options) {
      if (!option) continue;
      if (Array.isArray(option?.children) && option.children.length > 0) {
        const match = option.children.find((child: any) => child?.value === metricValue);
        if (match) {
          return option?.value;
        }
      }
      if (option?.metric?.value === metricValue) {
        return option?.value;
      }
    }
    return '';
  }, [metricValue, options]);

  const segmentValue = useMemo(() => {
    if (!MetricsTablePageIds.includes(pageId as any) && metricValue) {
      return selectedDataSourceValue || dataSource?.value;
    }
    return dataSource?.value;
  }, [dataSource?.value, metricValue, pageId, selectedDataSourceValue]);

  const getIconCur = (item: any, sourcesValue: any) => {
    const ifHasAlineaSelected = sourcesValue?.includes?.('alinea');
    const isItemAlinea =
      item?.icon === 'alinea' ||
      item?.value === 'alinea' ||
      item?.dataSource === 'alinea' ||
      (typeof item?.value === 'string' && item.value.startsWith('alinea__'));
    if (ifHasAlineaSelected && isItemAlinea) {
      return 'alineawhite';
    }
    if (item?.icon) {
      return item?.icon;
    }
    const ifHasAmpere = sourcesValue?.includes?.('ampere');
    const ifHasNewzoo = sourcesValue?.includes?.('newzoo');
    const ifHasDatabrainSelected = sourcesValue?.includes?.('databrain');
    if (ifHasNewzoo && item?.value === 'newzoo') {
      return 'newzoowhite';
    }
    if (ifHasAmpere && item?.value === 'ampere') {
      return 'amperewhite';
    }
    if (ifHasDatabrainSelected && item?.value === 'databrain') {
      return 'databrainbgresized';
    }
    return item?.dataSourceIcon || item?.value;
  };

  // Segment 选项
  const dataSourcesOptions = useMemo(() => {
    // console.log('metricValuemetricValue', metricValue);
    // console.log('options', options);
    // console.log('dataSource', dataSource);
    if (!Array.isArray(options)) return [];
    return options.map((item) => {
      const message = getMessage(item);
      const sourcesValue = segmentValue || dataSource?.value;
      const imgSrcDataSource = getDataSourceIcon(getIconCur(item, sourcesValue));
      const text =
        item?.disabled && message ? (
          <AggregationBubble
            DomOrText={
              <>
                <div style={{ margin: '0 -20px', padding: '0 20px' }}>
                  {item?.ifNoImg ? null : (
                    <img
                      style={{ position: 'relative', top: 3, right: 5, width: 15 }}
                      src={imgSrcDataSource}
                    />
                  )}
                  <Text>{formatMessage({ id: item?.text })}</Text>
                </div>
              </>
            }
            customBubbleContent={message}
            hasPointer={false}
          />
        ) : (
          <div>
            {item?.ifNoImg ? null : (
              <img
                style={{ position: 'relative', top: 3, right: 5, width: 15 }}
                src={imgSrcDataSource}
              />
            )}
            <Text>{formatMessage({ id: item?.text })}</Text>
          </div>
        );
      return {
        text,
        disabled: item?.disabled,
        value: item?.value,
      };
    });
  }, [isCN, options, locale, dataSource, segmentValue]);

  // Metrics Select 选项
  const metricsOptions = useMemo(() => {
    if (!dataSource) return [];
    if (!Array.isArray(dataSource.children) || !dataSource.children.length) return [];
    return dataSource.children.map((item: any) => {
      const message = getMessage(item);
      const dataSourceIcon = item?.icon || dataSource?.dataSourceIcon || dataSource?.value;
      return {
        value: item?.value,
        text: (
          <>
            <Bubble
              dark
              className="bubble-all-bg"
              overlayClassName="topcharts-download-bubble"
              trigger="hover"
              placement="top"
              content={item?.disabled ? message || '' : ''}
            >
              <div className="databrain__region-item">
                {item?.ifNoImg ? null : (
                  <img
                    style={{ width: '16px', height: '16px', marginRight: 4 }}
                    src={getDataSourceIcon(dataSourceIcon)}
                  />
                )}
                {/* <span className="databrain__region-item-ellipsis">{item?.text}</span> */}
                <span className="databrain__region-item-ellipsis">
                  {formatMessage({ id: item?.text })}
                </span>
              </div>
            </Bubble>
          </>
        ),
        disabled: item?.disabled || false,
      };
    });
  }, [isCN, dataSource]);

  const handleDataSourceChange = (value: string) => {
    // console.log('handleDataSourceChange-value', value)
    // console.log('handleDataSourceChange-options', options)
    const dataSource = options.find((item) => item.value === value);
    if (!dataSource) return;
    setDataSource(dataSource);
    if (!Array.isArray(dataSource.children) || !dataSource.children.length) {
      const { metric } = dataSource;
      setMetric(metric?.value);
      onChange({
        metric: metric?.value,
        source: value,
        metricname: metric?.text,
        changeTarget: 'sourceChange',
        isChange: true,
        currentMetricObj: metric,
        tableSource: dataSource?.tableSource,
      });
      return;
    }
    // const defaultMetric = dataSource.children[0];
    const childList = dataSource?.children || [];
    // 返回第一个没有禁选的选项
    const defaultMetric = childList.find((item: any) => !item.disabled);
    // console.log('handleDataSourceChange-defaultMetric', defaultMetric)
    if (!defaultMetric) return;
    setMetric(defaultMetric);
    onChange({
      metric: defaultMetric.value,
      source: value,
      metricname: defaultMetric.text,
      changeTarget: 'sourceChange',
      isChange: true,
      currentMetricObj: defaultMetric,
      tableSource: dataSource?.tableSource,
    });
  };

  const handleSelectChange = (value: string) => {
    if (!dataSource || !Array.isArray(dataSource.children)) return;
    const metric = dataSource.children.find((item: any) => item.value === value);
    if (!metric) return;
    setMetric(metric);
    onChange({
      metric: value,
      source: dataSource?.value || '',
      metricname: metric.text,
      changeTarget: 'seletChange',
      currentMetricObj: metric,
      isChange: true,
      tableSource: dataSource?.tableSource,
    });
  };

  // segment禁用时，更新value为 第一个非禁用的segment， segment都禁用时 保持不变
  useEffect(() => {
    if (!Array.isArray(options) || loading) return;
    // 找出当前指标对应的值为非禁用时不用走下面逻辑
    let currentMetricItem: any = {};
    let currentDataSourceItem: any = {};
    try {
      for (let i = 0; i < options.length; i++) {
        const item = options[i]?.children?.find((metricItem: any) => {
          return metricItem?.value === metricValue;
        });
        if (item) {
          currentMetricItem = item;
          currentDataSourceItem = options[i];
          setDataSource(options[i]);
          setMetric(item);
          break;
        }
      }
    } catch (err) {}
    // 如果一级数据源不禁用 && 二级指标不禁用，才可正常返回
    if (
      JSON.stringify(currentMetricItem) !== '{}' &&
      currentDataSourceItem?.disabled !== true &&
      currentMetricItem?.disabled !== true
    ) {
      return;
    }
    // 当所选的指标被禁用时，更新value为第一个非禁用值
    for (let i = 0; i < options.length; i++) {
      const dataSource = options[i];
      if (
        !dataSource ||
        dataSource.disabled ||
        !Array.isArray(dataSource.children) ||
        !dataSource.children.length
      )
        continue;
      const metrics = dataSource.children;
      for (let j = 0; j < metrics.length; j++) {
        const metric = metrics[j];
        if (!metric || metric.disabled) continue;
        setDataSource(dataSource);
        setMetric(metric);
        onChange({
          metric: metric.value,
          source: dataSource.value,
          metricname: metric.text,
          isChange: false,
          currentMetricObj: metric,
          tableSource: dataSource?.tableSource,
        });
        return;
      }
    }
  }, [JSON.stringify(options), metricValue, loading]);

  return (
    <div className="dbi-pcGame-segment-box-nav-wrap">
      <Tabs
        className={'dbi-pcGame-segment-box dbi-pcGame-segment-box-nav'}
        activeId={segmentValue}
        onActive={(tab: any) => handleDataSourceChange(tab?.id || tab)}
        tabs={dataSourcesOptions.map((opt) => ({
          id: opt.value,
          label: opt.text,
          disabled: opt.disabled,
        }))}
      />
      {dataSource?.children?.length > 0 && (
        <SelectChoice
          classModalName="dbi-pcGame-segment-box"
          overlayClassName="dbi-pcGame-segment-box-overlay"
          labelValue={formatMessage({ id: 'dbi_label_metrics' })}
          optionsList={metricsOptions}
          changeValue={metric?.value}
          getSelectMultiple={handleSelectChange}
          btnImg={getDataSourceIcon(getIconCur({ value: segmentValue || dataSource?.value }, segmentValue || dataSource?.value))}
          metricText={metric?.text}
          listWidth={
            dataSource?.value === 'newzoo'
              ? 340
              : dataSource?.value === 'Mscience'
              ? 320
              : dataSource?.value === 'famitsu'
              ? 410
              : 334
          }
        />
      )}
    </div>
  );
}
